import { createBaseApi } from '../baseApi';
import { setCredentials } from '../../slices/authSlice';

const authApi = createBaseApi('authApi');

export const enhancedAuthApi = authApi.injectEndpoints({
  endpoints: (builder) => ({
    getCurrentUser: builder.query({
      query: () => ({
        url: `/users/me`,
        method: 'GET'
      }),
      providesTags: ['User']
    }),
    login: builder.mutation({
      query: (credentials) => ({
        url: '/auth/local',
        method: 'POST',
        body: credentials
      }),
      invalidatesTags: ['Auth', 'User']
    }),
    logout: builder.mutation({
      query: () => ({
        url: '/auth/logout',
        method: 'POST'
      }),
      async queryFn(_, { dispatch }, _extraOptions, baseQuery) {
        return { data: { success: true } };
      },
      invalidatesTags: ['Auth', 'User']
    }),
    register: builder.mutation({
      query: (userData) => ({
        url: '/auth/local/register',
        method: 'POST',
        body: userData
      }),
      invalidatesTags: ['Auth', 'User']
    }),
    // Password management endpoints
    customRequestPasswordReset: builder.mutation({
      query: (email) => ({
        url: '/auth/custom-forgot-password',
        method: 'POST',
        body: { email }
      })
    }),
    customResetPassword: builder.mutation({
      query: ({ token, password, passwordConfirmation }) => ({
        url: '/auth/custom-reset-password',
        method: 'POST',
        body: { token, password, passwordConfirmation }
      }),
      invalidatesTags: ['Auth', 'User']
    }),
    // Email verification endpoints
    customSendVerificationEmail: builder.mutation({
      query: (email) => ({
        url: '/auth/custom-send-email',
        method: 'POST',
        body: { email }
      })
    }),
    customVerifyEmail: builder.mutation({
      query: (token) => ({
        url: '/auth/custom-verify-email',
        method: 'POST',
        body: { token }
      }),
      invalidatesTags: ['Auth', 'User']
    }),
    // Social login endpoints
    getGoogleAuthUrl: builder.query({
      query: (returnTo = '/dashboard') => ({
        url: `/auth/social-auth-url/google?return_to=${encodeURIComponent(returnTo)}`,
        method: 'GET'
      })
    }),
    // Get user info after social login with token
    getCurrentUserAfterSocialLogin: builder.mutation({
      query: (token) => ({
        url: '/users/me',
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      }),
      async onQueryStarted(token, { dispatch, queryFulfilled }) {
        try {
          const { data: user } = await queryFulfilled;
          // After getting user info, set complete credentials
          dispatch(setCredentials({ user, token }));
        } catch (error) {
          console.error('Failed to get user info after social login:', error);
        }
      },
      invalidatesTags: ['Auth', 'User']
    }),
  }),
  overrideExisting: false,
});

// Note: The logout mutation doesn't actually make an API call.
// It's implemented as a client-side only action that returns a successful response.
// The actual logout logic is handled in the authSlice.js file, which clears tokens and user data.

export const {
  useGetCurrentUserQuery,
  useLoginMutation,
  useLogoutMutation,
  useRegisterMutation,
  useCustomRequestPasswordResetMutation,
  useCustomResetPasswordMutation,
  useCustomSendVerificationEmailMutation,
  useCustomVerifyEmailMutation,
  useGetGoogleAuthUrlQuery,
  useGetCurrentUserAfterSocialLoginMutation,
} = enhancedAuthApi;

export default enhancedAuthApi;

